/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';
import type { ApiListResponse } from '@mairistem/administres-utils';

import { BASE_URL_ETATCIVIL, FILTRE_NUMERISES_ACTES } from '@/constants';
import type { Acte, TypeActe } from '@/types';

interface Meta {
  currentPage: number;
  lastPage: number;
}

/**
 * Récupère la liste des actes du type donné avec pagination.
 * Appeler `get(params)` pour déclencher la recherche,
 * les résultats s'accumulent page par page via le scroll infini.
 */
const useActes = (typeActe: TypeActe) => {
  const [loading, setLoading] = useState(false);
  const [datas, setDatas] = useState<Record<number, Array<Acte>>>({});
  const [metas, setMetas] = useState<Meta | null>(null);

  const reset = useCallback(() => {
    setDatas({});
    setMetas(null);
    setLoading(false);
  }, []);

  const get = useCallback(
    (params?: Record<string, unknown>) => {
      setLoading(true);

      const filteredParams = Object.fromEntries(
        Object.entries(params || {}).filter(([, value]) => value !== ''),
      );

      const queryString = new URLSearchParams({
        perPage: '15',
        // Actes numérisés seulement (AD-12901) ; les pacs n'ont pas ce filtre.
        ...(typeActe !== 'pacs' && FILTRE_NUMERISES_ACTES),
        ...(filteredParams as Record<string, string>),
      }).toString();

      const endpoint =
        typeActe === 'pacs' ? 'conventions' : `actes-${typeActe}`;
      const url = queryString
        ? `${BASE_URL_ETATCIVIL}/${endpoint}?${queryString}`
        : `${BASE_URL_ETATCIVIL}/${endpoint}`;

      const req = request.get<unknown, ApiListResponse<Acte>>(url);

      req
        .then(({ data, meta }) => {
          setDatas((prev) => ({
            ...prev,
            [meta.currentPage]: data.map((acte) => ({
              ...acte,
              type: typeActe,
            })),
          }));
          setMetas(meta as Meta);
        })
        .catch(() => {
          toast.error('Une erreur est survenue lors du chargement des actes');
        })
        .finally(() => {
          setLoading(false);
        });

      return req;
    },
    [typeActe],
  );

  return {
    data: useMemo(() => _.flatten(_.values(datas)), [datas]),
    meta: metas,
    loading,
    get,
    reset,
  };
};

export default useActes;
